/**
 * "Why this match" helpers (WP1.4).
 *
 * The backend builds the explanation (`apps/match_requests/explanation.py`
 * holds the status / confidence rules); the UI only formats it. Company
 * responses never carry the supplier's compliance number — that line's
 * `score` is null and its `detail` is the trust-badge fact.
 */
import i18n from "../i18n";
import { formatNumber } from "../i18n/format";
import type {
  ConfidenceEnum,
  MatchExplanationLine,
  MatchExplanationLineStatusEnum,
} from "../services/match-requests/generated/model";

export type { MatchExplanationLine };
export type MatchConfidence = ConfidenceEnum;

export const explanationUnavailableLabel = (): string => i18n.t("domain:explanation.unavailable");

export const explanationStatusLabel = (status: MatchExplanationLineStatusEnum): string =>
  i18n.t(`domain:explanation.status.${status}`);

export const STATUS_ICONS: Record<MatchExplanationLineStatusEnum, string> = {
  matched: "fa-solid fa-circle-check",
  partial: "fa-solid fa-circle-half-stroke",
  missing: "fa-solid fa-circle-xmark",
  not_assessed: "fa-regular fa-circle-question",
};

export const confidenceLabel = (confidence: ConfidenceEnum): string =>
  i18n.t(`domain:explanation.confidence.${confidence}`);

/** "82%" for a scored line, null when there is no number to show. */
export function explanationScore(line: MatchExplanationLine): string | null {
  return typeof line.score === "number"
    ? formatNumber(line.score, { style: "percent", maximumFractionDigits: 0 })
    : null;
}

/** The lines the buyer weighted most — used for the compact card summary. */
export function topExplanationLines(lines: readonly MatchExplanationLine[], count = 3): MatchExplanationLine[] {
  return [...lines].sort((a, b) => b.weight - a.weight).slice(0, count);
}
